import {
    TransactionRuleGroup,
    TransactionRule,
} from "../../types/transactionRule";
import { RuleScopeContext, RuleWarnings, EMPTY_RULE_WARNINGS } from "./types";
import { indexRule } from "./indexing";
import { isRuleUnreachableAgainst } from "./subsetChecks";
import { isRuleDuplicateAgainst } from "./identicalChecks";

/**
 * Compute override warnings for every rule in a group in one pass.
 * Complexity: O(N^2 * C) per group where N = rule count, C = average
 * conditions per rule. Intended to be invoked once per data change and the
 * resulting map reused.
 */
export function computeRuleGroupWarnings(
    group: TransactionRuleGroup,
    ctx: RuleScopeContext,
): Map<string, RuleWarnings> {
    const result = new Map<string, RuleWarnings>();
    const rules = group.rules ?? [];
    if (rules.length === 0) {
        return result;
    }

    const indexed = rules.map((r) => indexRule(r, ctx));
    const seenIds = new Set<string>();

    for (let i = 0; i < indexed.length; i++) {
        const lower = indexed[i];
        const ruleId = lower.rule.id;

        if (seenIds.has(ruleId)) {
            continue;
        }
        seenIds.add(ruleId);

        if (i === 0) {
            result.set(ruleId, { ...EMPTY_RULE_WARNINGS });
            continue;
        }
        let isUnreachable = false;
        let isDuplicate = false;
        for (let j = 0; j < i; j++) {
            const higher = indexed[j];
            if (higher.rule.isDisabled) {
                continue;
            }
            if (!isDuplicate && isRuleDuplicateAgainst(lower, higher)) {
                isDuplicate = true;
            }
            if (
                !isUnreachable &&
                isRuleUnreachableAgainst(lower, higher, ctx)
            ) {
                isUnreachable = true;
            }
            if (isDuplicate && isUnreachable) {
                break;
            }
        }
        result.set(ruleId, { isUnreachable, isDuplicate });
    }

    return result;
}

/**
 * Single-rule convenience wrapper. Prefer `computeRuleGroupWarnings` in
 * components so the work is done once per group render.
 */
export function getRuleWarnings(
    rule: TransactionRule,
    group: TransactionRuleGroup,
    ctx: RuleScopeContext,
): RuleWarnings {
    return (
        computeRuleGroupWarnings(group, ctx).get(rule.id) ?? EMPTY_RULE_WARNINGS
    );
}
